import { useState } from "react";
import { Trash2Icon } from "lucide-react";

import { ConfirmationDialog } from "@/components/ConfirmationDialog";

import { useDeleteRemoveExploreImages } from "@/hooks/deleteRemoves/useDeleteRemoveExploreImages";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { chamferClipPath } from "@/lib/exploreImages";

// Floats over the gallery while selecting, so the delete stays in reach
// however far down the selection goes.
export const ExploreImageBulkDeleteBar = ({
  onCancel,
  onDeleted,
  selectedIds,
}: {
  onCancel: () => void;
  onDeleted: () => void;
  selectedIds: string[];
}) => {
  /* Database CRUD Operations */
  // Delete
  const deleteExploreImages = useDeleteRemoveExploreImages();

  /* React Hooks */
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const showSuccessToast = useSuccessToast();

  /* Helpers */
  const count = selectedIds.length;
  const imagesLabel = count === 1 ? "image" : "images";

  /* Handlers */
  const handleDelete = () => {
    deleteExploreImages.mutate(
      { ids: selectedIds },
      {
        onSuccess: () => {
          setIsConfirmOpen(false);
          showSuccessToast({
            description: `Deleted ${count.toString()} ${imagesLabel}.`,
          });
          onDeleted();
        },
      },
    );
  };

  /* Render */
  return (
    <>
      <div
        className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 items-center gap-1 bg-[#212327] p-1.5 shadow-[0_20px_50px_-15px_rgba(0,0,0,0.6)] dark:bg-[#FAFAFA]"
        style={{ clipPath: chamferClipPath(10) }}
      >
        <button
          className="px-3 py-2 font-plex-mono text-[11px] uppercase tracking-[0.12em] text-white/70 transition-colors hover:text-white dark:text-[#212327]/70 dark:hover:text-[#212327]"
          onClick={onCancel}
          type="button"
        >
          Cancel
        </button>
        <button
          className="flex items-center gap-2 bg-red-600 px-4 py-2 font-plex-mono text-[11px] uppercase tracking-[0.16em] text-white transition-colors hover:bg-red-500 disabled:bg-white/10 disabled:text-white/40 dark:disabled:bg-black/10 dark:disabled:text-black/40"
          disabled={!count}
          onClick={() => {
            setIsConfirmOpen(true);
          }}
          style={{ clipPath: chamferClipPath(6) }}
          type="button"
        >
          <Trash2Icon className="h-3.5 w-3.5" />
          Delete ({count})
        </button>
      </div>

      <ConfirmationDialog
        confirmButtonText={`Delete ${count.toString()}`}
        confirmationButtonVariant="destructive"
        description={`This removes ${count.toString()} ${imagesLabel} from every category in the worker app.`}
        errorMessage="Failed to delete the images."
        isError={deleteExploreImages.isError}
        isLoading={deleteExploreImages.isPending}
        onConfirm={handleDelete}
        onOpenChange={setIsConfirmOpen}
        open={isConfirmOpen}
        title={`Delete ${count.toString()} ${imagesLabel}?`}
      />
    </>
  );
};
